// HOW TO USE
// 1. put it in template and give it a ref name, such as
//   <s-encrypt-password-dialog ref="passwordDialog" />
// 2. await getPassword function and wait for password
//   let password = await this.$refs.passwordDialog.getPassword()
// 3. Pass ['encryptedFileKey', 'keyPairId'] through vuex commonComponentModule
//    encryptedFileKey: 密件對應的Key, 此Key被User publicKey加密
//    keyPairId: 上述User publicKey對應的KeyPairId
//
// BEHAVIOR
// user cannot decrypt file in the following situations
// 1. user don't set up key pair
// 2. user isn't opener (need to set KeyPair = -1)
// 3. key pair id was changed after the file encrypted

<template>
  <v-dialog
    v-model="show"
    persistent
    max-width="300px"
  >
    <v-card v-if="isLoadingPrivateKey">
      <v-skeleton-loader
        type="article, actions"
      />
    </v-card>
    <v-card v-else-if="!localKeyPairId">
      <v-card-title>{{ $t('encrypt.msgUserKeyPairNotSetup') }}</v-card-title>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          color="primary"
          @click="notified('user is not opener')"
        >
          {{ $t('ConfirmModal.OK') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else-if="keyPairId=== -1">
      <v-card-title>{{ $t('encrypt.userIsNotOpener') }}</v-card-title>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          color="primary"
          @click="notified('user is not opener')"
        >
          {{ $t('ConfirmModal.OK') }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-form
      v-else-if="localKeyPairId === keyPairId"
      @submit.prevent="confirm"
    >
      <v-card>
        <v-card-title
          class="text-body-1"
        >
          {{ $t('encrypt.pleaseEnterEncryptedFilePassword') }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="passphrase"
            :append-icon="showPassword ? mdiEye : mdiEyeOff"
            :rules="rules.required"
            :type="showPassword ? 'text' : 'password'"
            :label="$t('encrypt.encryptedFilePassword')"
            autocomplete="encrypt-password"
            :error-messages="passphraseErrorMsg"
            @click:append="showPassword = !showPassword"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            color="primary"
            @click="cancel"
          >
            {{ $t('Text.Cancel') }}
          </v-btn>
          <v-btn
            text
            color="primary"
            type="submit"
          >
            {{ $t('ConfirmModal.OK') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-card v-else>
      <v-card-title>{{ $t('encrypt.userIsOpenerButKeyPairIdNotMatch') }}</v-card-title>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          color="primary"
          @click="notified('key pair id not match')"
        >
          {{ $t('ConfirmModal.OK') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VuetifyJetValidator from 'vuetify-jet-validator'
import { mapGetters, mapMutations } from 'vuex'
import { commonComponentModule } from '@/store/moduleName.js'
import { mdiEye, mdiEyeOff } from '@mdi/js'
import CryptoUtil from '@/lib/g2e/CryptoUtil.js'
import jwtTokenApi from '@/api/general/jwtToken.js'
import messageUtil from '@/utils/messageUtil'

const validator = new VuetifyJetValidator()

let resolveFn = null // function
let rejectFn = null // function

export default {
  props: {
    // encryptedFileKey: {
    //   type: String,
    //   required: true
    // }
  },
  data: function () {
    return {
      show: false,
      showPassword: false,
      passphrase: null,
      isLoadingPrivateKey: false,
      localKeyPairId: null,
      privateKey: null,
      passphraseErrorMsg: null,
      mdiEye,
      mdiEyeOff,
      rules: {
        required: [
          validator.rules.required(this.$t('TrainingClient.RequiredField'))
        ]
      }
    }
  },
  computed: {
    ...mapGetters(commonComponentModule, ['decryptedFileKey', 'encryptedFileKey', 'keyPairId'])
    // ...mapState({
    //   decryptedFileKey: state => state[commonComponentModule].decrypted.fileKey
    // })
  },
  watch: {
    passphrase: function () { // clear error msg
      this.passphraseErrorMsg = null
    }
  },
  mounted: async function () {
    // console.log('this.encryptedFileKey : ' + this.encryptedFileKey)
    if (!this.privateKey) {
      await this.getPrivateKey()
      // console.log('privateKey: ' + this.privateKey)
    }

    // error handle
    if (!this.privateKey) {
      console.error('privateKey is null')
    } else {
      if (!this.encryptedFileKey) {
        console.error('encryptedFileKey is null')
      } else if (!this.keyPairId) {
        console.error('kepPairId is null')
      }
    }
  },
  methods: {
    ...mapMutations(commonComponentModule, ['setDecryptedFileKey']),
    async getPassword () { // return promise
      if (this.decryptedFileKey) {
        return new Promise((resolve) => {
          resolve(this.decryptedFileKey)
        })
      } else {
        this.show = true
        return new Promise((resolve, reject) => {
          resolveFn = resolve
          rejectFn = reject
        })
      }
    },
    cancel () {
      rejectFn(new Error('user cancel'))
      this.show = false
      this.passphrase = null
    },
    notified (msg) {
      rejectFn(new Error(msg))
      this.show = false
    },
    async confirm () {
      let fileKey
      try {
        fileKey = await CryptoUtil.PGPTool.decryptText(this.encryptedFileKey, this.privateKey, this.passphrase)
      } catch (err) {
        if (err.message.includes('Incorrect key passphrase')) {
          // console.log('Incorrect key passphrase')
          this.passphraseErrorMsg = this.$t('encrypt.wrongPrivateKeyPassphrase')
          return
        } else {
          console.error(err)
        }
      }
      // console.log(this.privateKey)
      // this.$store.commit(commonComponentModule + '/setDecryptedFileKey', fileKey) // cache
      this.setDecryptedFileKey(fileKey) // cache
      resolveFn(fileKey)
      this.show = false
      this.passphrase = null
    },
    async getPrivateKey () {
      this.isLoadingPrivateKey = true
      await jwtTokenApi.getPrivateKey(resp => {
        if (resp.status === 201 || resp.status === 200) {
          this.localKeyPairId = resp.data.id
          this.privateKey = resp.data.privateKey
        } else {
          messageUtil.httpStatusHandler(resp, this)
        }
        this.isLoadingPrivateKey = false
      })
      // this.encryptedPrivate = $http.get()
      //       return `-----BEGIN PGP PRIVATE KEY BLOCK-----

      // xYYEYdK+yRYJKwYBBAHaRw8BAQdAzLB+PqcDdvFbHfFkFV4Y/vq72HuzLUO0
      // 0ddEQS6P14X+CQMIVwT5LrFntUPgMqeYxiAcUB62b6Kv+GeE3nVQcuwDM1Ej
      // plffXJQBQbAxsyMHysKquqPOKcB1BaU3tRGNNfX/GBOL7CG3Apq91goYxyu7
      // pM0AwowEEBYKAB0FAmHSvskECwkHCAMVCAoEFgECAAIZAQIbAwIeAQAhCRCM
      // 5svY61KeGhYhBESTF/uwo3kN7vcQpozmy9jrUp4aEqwA/0T1Z1Y76HhhEZpA
      // jNsvW1UVNL26YEVC/YSfPzVD+4oaAP426/+zlj8grpl9AfEljiTS6n0aa2OB
      // /akEPX0eqr/0DMeLBGHSvskSCisGAQQBl1UBBQEBB0D8odu0xxV18vycH5fW
      // fgF1KjYpBWAt4sNPc+Q1HjTaVAMBCAf+CQMIBDteW5yEJHLgG97iaAadLwXa
      // sa0veEfxqdK6Ifn0Q+ucTNWGm7j4uLka2oP0Y7Fj0O2Vm+/GGrgm1yoN+s3f
      // AvxU/9Tv1zkg0Nu3NKvFVsJ4BBgWCAAJBQJh0r7JAhsMACEJEIzmy9jrUp4a
      // FiEERJMX+7CjeQ3u9xCmjObL2OtSnhovIgEAzvJlNHSKZB/0fqLg7PJ4W9Wu
      // ggtnzTS03NJtIWATSlMA/ReGQJ4ZdY9fQYcrkuGSOyNed1Qgvn75HGOtmhcr
      // BYkA
      // =GMB7
      // -----END PGP PRIVATE KEY BLOCK-----
      // `
    }
  }
}
</script>
